import { sectionLabels } from "../section-labels";
/**
 * The restaurant storefront's masthead: brand mark, name, tagline, and the
 * strip of copy some restaurants run above it ("DOUBLE UP TUESDAY — ANY DOUBLE
 * FOR THE PRICE OF A SINGLE").
 *
 * The announcement strip is a FIELD here rather than the existing
 * `announcement-bar` section. That section is chrome: `BuyerShell` renders it
 * above the header and ignores its position in the section list entirely. A
 * restaurant skin draws its own shell, so there is no `BuyerShell` to hand it
 * to — and the strip is part of the masthead's composition (STACK inverts it
 * to the ink colour and butts it against the nav), not a free-floating band.
 *
 * `locked`: every skin opens with it, and a menu page whose customer cannot
 * tell which restaurant they are looking at has failed at the only job the
 * page has.
 */
export const restaurantHeaderSchema = {
    type: "restaurant-header",
    name: sectionLabels["restaurant-header"],
    description: {
        ar: "اسم المطعم وشعاره، وشريط الإعلان اللي فوقه.",
        en: "The restaurant's name and mark, plus the announcement strip above it.",
    },
    locked: true,
    criticalForSeo: true,
    maxPerTemplate: 1,
    availableOnTemplates: ["home"],
    availableOnStoreKinds: ["RESTAURANT"],
    settings: [
        {
            id: "tagline",
            type: "text",
            i18n: true,
            label: { ar: "الوصف المختصر", en: "Tagline" },
            helpText: {
                ar: "سطر قصير تحت الاسم — نوع المطبخ والمكان مثلًا.",
                en: "One short line under the name — the cuisine and the neighbourhood.",
            },
            validate: { max: 60 },
        },
        {
            id: "showAnnouncement",
            type: "checkbox",
            label: { ar: "إظهار شريط الإعلان", en: "Show the announcement strip" },
            default: false,
        },
        {
            id: "announcementText",
            type: "text",
            i18n: true,
            label: { ar: "نص الإعلان", en: "Announcement" },
            helpText: {
                ar: "عرض النهارده أو خبر قصير. سيبه فاضي عشان الشريط يختفي.",
                en: "Today's offer or a short notice. Leave empty to drop the strip.",
            },
            validate: { max: 120 },
            showIf: { showAnnouncement: true },
        },
        {
            /**
             * The cocked badge on the promo card ("TODAY").
             *
             * Phone only — the desktop announcement is a thin strip with no room for
             * it. Empty ⇒ the card is just its two lines, which is how a restaurant
             * with a standing offer rather than a dated one should read.
             */
            /**
             * The phone promo card's SECOND line.
             *
             * The artboards use different copy on the two surfaces: the desktop bar
             * runs the whole offer on one line ("… · today only · Zamalek & Maadi"),
             * while the phone card sets a short headline over a single explanatory
             * line. Splitting the bar's string on its dash gave the card a subtitle
             * that wrapped to three lines and pushed the badge out of the card.
             *
             * Absent ⇒ the card falls back to the remainder of the announcement, so
             * a theme that sets no subtext behaves exactly as before.
             */
            id: "announcementSubtext",
            type: "text",
            i18n: true,
            label: { ar: "سطر الإعلان الثاني", en: "Announcement second line" },
            validate: { max: 60 },
            showIf: { showAnnouncement: true },
        },
        {
            id: "announcementPill",
            type: "text",
            i18n: true,
            label: { ar: "شارة الإعلان", en: "Announcement badge" },
            validate: { max: 14 },
            showIf: { showAnnouncement: true },
        },
        {
            id: "signInLabel",
            type: "text",
            i18n: true,
            label: { ar: "زرار الدخول", en: "Sign-in button" },
            helpText: {
                ar: "على الموقع العام يفتح تسجيل دخول العميل، أو حسابه بعد تسجيل الدخول. يبقى غير نشط على الطاولة وفي المعاينة.",
                en: "On the public website it opens the customer login, or the account once logged in. Inert at a table and in the preview.",
            },
            validate: { max: 24 },
        },
        {
            /**
             * The masthead's own call to action ("ORDER PICKUP").
             *
             * It lives on the HEADER rather than in `restaurant-order-cta` because
             * that is where the design puts it — inside the nav row, as its loudest
             * element. A separate section could not be composed into a nav.
             *
             * Pickup is Open Decision 1, so this renders as a styled, INERT element
             * on a live storefront and carries the "not live yet" badge in the theme
             * preview. Empty ⇒ the nav ends after the links.
             */
            id: "ctaLabel",
            type: "text",
            i18n: true,
            label: { ar: "زر الترويسة", en: "Header button" },
            helpText: {
                ar: "لسه مش شغّال — بيتعرض من غير ما يشتغل.",
                en: "Not built yet — renders, but does nothing when tapped.",
            },
            validate: { max: 24 },
        },
    ],
    blocks: [
        {
            type: "restaurant-nav-link",
            name: { ar: "رابط", en: "Nav link" },
            description: {
                ar: "كلمة في شريط الترويسة.",
                en: "One word in the masthead's nav row.",
            },
            // Four is the point at which the row starts wrapping at 1180px, which is
            // the width the design is drawn at.
            maxPerSection: 4,
            settings: [
                {
                    id: "label",
                    type: "text",
                    i18n: true,
                    label: { ar: "النص", en: "Label" },
                    validate: { max: 18 },
                },
                {
                    id: "href",
                    type: "url",
                    label: { ar: "الرابط", en: "Link" },
                    helpText: {
                        ar: "سيبه فاضي لو الرابط بينزل لمكان في نفس الصفحة.",
                        en: "Leave empty for a link that just scrolls down the page.",
                    },
                },
            ],
        },
    ],
};
//# sourceMappingURL=restaurant-header.js.map